import i18n from '@/utils/i18n';
import { formatCurrency } from '@/utils/money';
import { formatPercentage } from '@/experiences/sales-portal/blocs/utils/money';
import { inArray, useLiveQuery } from '@platform/db/tanstack-db';
import { orderGuideItemCollection } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/collections/order-entry-collection';
import { PricingCalculations } from '@/utils/pricing-calculations';
import { SavedCartItem } from '@/experiences/sales-portal/domain/ordering/blocs/stores/cart-store';
import { useConfidentialVisibilityStore } from '@/experiences/sales-portal/domain/ordering/blocs/stores/order-entry-confidential-visibility-store';
import { useDeferredValue, useMemo } from 'react';

export type OrderMetricKey =
  | 'commission'
  | 'gp_amount'
  | 'gp_percentage'
  | 'subtotal'
  | 'total_items'
  | 'total_units';

export interface OrderMetric {
  isLoading?: boolean;
  metricKey: OrderMetricKey;
  title: string;
  value: string;
}

interface CommissionOptions {
  /** Formatted commission display value (e.g. "$12.34") */
  displayValue?: string | null;
  /** Whether commission feature is enabled for this user/supplier */
  isEnabled?: boolean;
  /** Whether commission data is currently loading or stale */
  isLoading?: boolean;
}

interface UseOrderMetricsOptions {
  /** Commission metric configuration */
  commission?: CommissionOptions;
  /** Currency code used to format currency metrics */
  currencyCode: string;
  /** When true, GP% and GP$ metrics are excluded from the result */
  isGrossProfitHidden?: boolean;
}

/**
 * Hook to calculate order metrics for the order entry screen.
 * Queries the local DB to retrieve pricing information for cart items.
 *
 * @param cartItems {SavedCartItem[]} - The items in the cart
 * @param options - Configuration
 * @param options.currencyCode - Currency code (from the supplier org) used to format currency metrics
 * @param options.commission - Commission metric configuration (display value, enabled state, loading state)
 * @param options.isGrossProfitHidden - When true, GP% and GP$ metrics are excluded
 *
 * @returns {OrderMetric[]} Order metrics for the order entry screen
 *
 * @example
 * ```typescript
 * const cartItems = useCartStore(
 *   useShallow((state) => selectSalesPortalCartItems(state)),
 * );
 * const { currencyCode } = useSupplierCurrencyCode(supplierUUID);
 * const orderMetrics = useOrderMetrics(cartItems, { currencyCode });
 * ```
 */
export const useOrderMetrics = (
  cartItems: SavedCartItem[],
  options: UseOrderMetricsOptions,
): OrderMetric[] => {
  const { commission, currencyCode, isGrossProfitHidden } = options;
  const showConfidentialFields = useDeferredValue(
    useConfidentialVisibilityStore((state) => state.showConfidentialFields),
  );

  // Get variant pack UUIDs from cart items
  const variantPackUUIDs = useMemo(
    () => cartItems.map((item) => item.variantPackUUID),
    [cartItems],
  );

  // Defer to avoid blocking input during DB queries when adding new items
  const deferredVariantPackUUIDs = useDeferredValue(variantPackUUIDs);

  // Query the local DB to get pricing information for items in the cart
  const { data: pricingData } = useLiveQuery(
    (q) =>
      deferredVariantPackUUIDs.length > 0
        ? q
            .from({ orderGuideItemCollection })
            .where(({ orderGuideItemCollection: item }) =>
              inArray(item.variant_pack_id, deferredVariantPackUUIDs),
            )
        : null,
    [deferredVariantPackUUIDs],
  );

  // Build a price map from the order guide items for efficient lookups
  const priceMap = useMemo(() => {
    const map: Record<
      string,
      {
        currencyCode: string | null;
        packCost: number | null;
        packPrice: number | null;
      }
    > = {};

    for (const item of pricingData ?? []) {
      // Items are returned directly from the collection
      map[item.variant_pack_id] = {
        currencyCode: item.variant_pack_info?.currency ?? null,
        packCost: item.variant_pack_info?.pack_cost_in_micros ?? null,
        packPrice: item.variant_pack_info?.pack_price_in_micros ?? null,
      };
    }

    return map;
  }, [pricingData]);

  // Total items = count of unique variant packs (line items) with quantity > 0
  const totalItems = useMemo(
    () => cartItems.filter((item: SavedCartItem) => item.quantity > 0).length,
    [cartItems],
  );

  const totalUnits = useMemo(
    () =>
      cartItems.reduce(
        (acc: number, item: SavedCartItem) => acc + item.quantity,
        0,
      ),
    [cartItems],
  );

  const subtotalInMicros = useMemo(
    () =>
      cartItems.reduce(
        (acc: number, item: SavedCartItem) =>
          acc +
          item.quantity * (priceMap[item.variantPackUUID]?.packPrice ?? 0),
        0,
      ),
    [cartItems, priceMap],
  );

  // don't include items missing costing information for gp calculations
  const cartItemsWithCostingInformation = useMemo(() => {
    return cartItems.filter((item) => {
      return (
        priceMap[item.variantPackUUID]?.packCost !== null &&
        priceMap[item.variantPackUUID]?.packPrice !== null
      );
    });
  }, [cartItems, priceMap]);

  // compute total price in micros for items with costing information, used for gp calculations
  const totalPriceInMicros = useMemo(
    () =>
      cartItemsWithCostingInformation.reduce(
        (acc: number, item: SavedCartItem) =>
          acc +
          (priceMap[item.variantPackUUID]?.packPrice ?? 0) * item.quantity,
        0,
      ),
    [cartItemsWithCostingInformation, priceMap],
  );

  // compute total cost in micros for items with costing information, used for gp calculations
  const totalCostInMicros = useMemo(
    () =>
      cartItemsWithCostingInformation.reduce(
        (acc: number, item: SavedCartItem) =>
          acc + (priceMap[item.variantPackUUID]?.packCost ?? 0) * item.quantity,
        0,
      ),
    [cartItemsWithCostingInformation, priceMap],
  );

  // gross profit amount in micros = total price in micros - total cost in micros
  const grossProfitAmountInMicros = totalPriceInMicros - totalCostInMicros;

  // gross profit percentage = gross profit amount in micros / total price in micros
  const grossProfitPercentage = useMemo(() => {
    return (
      PricingCalculations.grossMarginPercentFromGrossProfit(
        grossProfitAmountInMicros,
        totalPriceInMicros,
      ) ?? 0
    );
  }, [totalPriceInMicros, grossProfitAmountInMicros]);

  const shouldShowGpMetrics = !isGrossProfitHidden && showConfidentialFields;

  const gpMetrics: OrderMetric[] = shouldShowGpMetrics
    ? [
        {
          metricKey: 'gp_percentage',
          title: i18n.t(
            'sales_portal.order_entry.order_summary_bottom_bar.metric.gp_percentage.title',
          ),
          value: formatPercentage(grossProfitPercentage),
        },
        {
          metricKey: 'gp_amount',
          title: i18n.t(
            'sales_portal.order_entry.order_summary_bottom_bar.metric.gp_amount.title',
          ),
          value: formatCurrency(grossProfitAmountInMicros, currencyCode),
        },
      ]
    : [];

  const commissionMetrics: OrderMetric[] =
    commission?.isEnabled && showConfidentialFields
      ? [
          {
            isLoading: commission.isLoading,
            metricKey: 'commission',
            title: i18n.t(
              'sales_portal.order_entry.order_summary_bottom_bar.metric.commission.title',
            ),
            value: commission.displayValue ?? formatCurrency(0, currencyCode),
          },
        ]
      : [];

  return [
    ...gpMetrics,
    {
      metricKey: 'total_items',
      title: i18n.t(
        'sales_portal.order_entry.order_summary_bottom_bar.metric.total_items.title',
      ),
      value: totalItems.toString(),
    },
    {
      metricKey: 'total_units',
      title: i18n.t(
        'sales_portal.order_entry.order_summary_bottom_bar.metric.total_units.title',
      ),
      value: totalUnits.toString(),
    },
    {
      metricKey: 'subtotal',
      title: i18n.t(
        'sales_portal.order_entry.order_summary_bottom_bar.metric.subtotal.title',
      ),
      value: formatCurrency(subtotalInMicros, currencyCode),
    },
    ...commissionMetrics,
  ];
};
